<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">项目列表</h1>
        <p class="text-sm text-slate-500 mt-1">查看和管理所有科研项目的智能评审业务流转进度</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">项目管理</span>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="glass-card rounded-2xl p-2">
      <div class="flex gap-2">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-300"
          :class="activeTab === tab.key ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' : 'text-slate-600 hover:bg-slate-100'"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="grid grid-cols-4 gap-4">
      <div class="premium-dark-card rounded-2xl p-5 relative overflow-hidden">
        <div class="stat-blob bg-blue-500"></div>
        <div class="relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
            <span class="text-xs text-slate-400 font-medium">项目总数</span>
          </div>
          <div class="number-font text-3xl font-bold text-white">156</div>
        </div>
      </div>

      <div class="premium-dark-card rounded-2xl p-5 relative overflow-hidden">
        <div class="stat-blob bg-emerald-500"></div>
        <div class="relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            <span class="text-xs text-slate-400 font-medium">已完成</span>
          </div>
          <div class="number-font text-3xl font-bold text-white">89</div>
        </div>
      </div>

      <div class="premium-dark-card rounded-2xl p-5 relative overflow-hidden">
        <div class="stat-blob bg-blue-500"></div>
        <div class="relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="text-xs text-slate-400 font-medium">进行中</span>
          </div>
          <div class="number-font text-3xl font-bold text-white">45</div>
        </div>
      </div>

      <div class="premium-dark-card rounded-2xl p-5 relative overflow-hidden">
        <div class="stat-blob bg-slate-500"></div>
        <div class="relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="text-xs text-slate-400 font-medium">未开始</span>
          </div>
          <div class="number-font text-3xl font-bold text-white">22</div>
        </div>
      </div>
    </div>

    <!-- Search and Filter -->
    <div class="flex items-center gap-4">
      <div class="flex-1 relative">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="搜索项目编号或项目名称..."
          class="premium-input w-full rounded-xl pl-10 pr-4 py-3 text-sm"
        />
        <svg class="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
      </div>
      <select v-model="filterStatus" class="premium-input rounded-xl px-4 py-3 text-sm min-w-[140px]">
        <option value="">全部状态</option>
        <option value="completed">已完成</option>
        <option value="inProgress">进行中</option>
        <option value="notStarted">未开始</option>
      </select>
      <button class="btn-action px-6 py-3 rounded-xl text-white font-medium text-sm flex items-center gap-2">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        新增项目
      </button>
    </div>

    <!-- Data Table -->
    <div class="glass-card rounded-3xl overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-slate-50/80 border-b border-slate-200">
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">项目编号</th>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">项目名称</th>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">负责人</th>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">状态</th>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">当前环节</th>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">查看过程记录</th>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">查看项目内容</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr v-for="(project, index) in filteredProjects" :key="project.id" class="hover:bg-slate-50/50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="text-sm font-medium text-slate-700">{{ project.code }}</span>
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-xs">
                    {{ project.name.charAt(0) }}
                  </div>
                  <span class="text-sm font-medium text-slate-700">{{ project.name }}</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="text-sm text-slate-600">{{ project.leader }}</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="px-3 py-1 rounded-full text-xs font-medium"
                  :class="{
                    'bg-emerald-50 text-emerald-600': project.status === 'completed',
                    'bg-blue-50 text-blue-600': project.status === 'inProgress',
                    'bg-slate-100 text-slate-500': project.status === 'notStarted'
                  }">
                  {{ statusText(project.status) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="text-sm text-slate-600">{{ project.currentStage }}</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <button class="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                  过程记录
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <button class="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                  查看
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-between px-6 py-4 border-t border-slate-200">
        <span class="text-sm text-slate-500">共 {{ filteredProjects.length }} 条记录</span>
        <div class="flex items-center gap-2">
          <button class="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50" disabled>上一页</button>
          <button class="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-sm font-medium">1</button>
          <button class="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">2</button>
          <button class="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">3</button>
          <button class="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">下一页</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectList',
  data() {
    return {
      activeTab: 'all',
      searchQuery: '',
      filterStatus: '',
      tabs: [
        { key: 'all', label: '全部项目' },
        { key: 'guideline', label: '指南凝练环节' },
        { key: 'preliminary', label: '项目初审环节' },
        { key: 'approval', label: '立项评审环节' },
        { key: 'supervision', label: '任务书审查环节' },
        { key: 'finalReview', label: '结题验收评审环节' }
      ],
      projects: [
        {
          id: 1,
          code: '2025AI001',
          name: '基于深度学习的智能诊断系统研究',
          leader: '张明华',
          status: 'completed',
          currentStage: '已完成申报书审查；已完成立项评审'
        },
        {
          id: 2,
          code: '2025AI002',
          name: '自然语言处理在医疗领域的应用',
          leader: '李晓峰',
          status: 'inProgress',
          currentStage: '正在进行任务书审查'
        },
        {
          id: 3,
          code: '2025BIO001',
          name: '新型抗肿瘤药物筛选平台构建',
          leader: '王建国',
          status: 'inProgress',
          currentStage: '正在进行中期审查'
        },
        {
          id: 4,
          code: '2025BIO002',
          name: '基因编辑技术在作物改良中的应用',
          leader: '陈志强',
          status: 'notStarted',
          currentStage: '无'
        },
        {
          id: 5,
          code: '2025MAT001',
          name: '高性能碳纤维复合材料制备技术',
          leader: '刘芳',
          status: 'completed',
          currentStage: '已完成结题审查'
        },
        {
          id: 6,
          code: '2025ENE001',
          name: '高效太阳能电池材料研发',
          leader: '赵伟',
          status: 'inProgress',
          currentStage: '正在进行年度报告审查'
        },
        {
          id: 7,
          code: '2025AI003',
          name: '计算机视觉在工业检测中的应用',
          leader: '孙丽',
          status: 'completed',
          currentStage: '已完成立项评审'
        },
        {
          id: 8,
          code: '2025BIO003',
          name: '合成生物学在医药领域的创新应用',
          leader: '周明',
          status: 'inProgress',
          currentStage: '正在进行申报书初审'
        },
        {
          id: 9,
          code: '2025MAT002',
          name: '纳米材料在环境治理中的应用研究',
          leader: '吴强',
          status: 'notStarted',
          currentStage: '无'
        },
        {
          id: 10,
          code: '2025ENE002',
          name: '氢能源存储与转化技术研究',
          leader: '郑华',
          status: 'inProgress',
          currentStage: '正在进行结题审查'
        },
        {
          id: 11,
          code: '2025AI004',
          name: '智能推荐算法在电商平台的优化',
          leader: '钱进',
          status: 'completed',
          currentStage: '已完成所有审查环节'
        },
        {
          id: 12,
          code: '2025BIO004',
          name: '微生物发酵工程优化研究',
          leader: '李梅',
          status: 'inProgress',
          currentStage: '正在进行过程检查'
        }
      ]
    }
  },
  computed: {
    filteredProjects() {
      let result = this.projects

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(p => 
          p.code.toLowerCase().includes(query) || 
          p.name.toLowerCase().includes(query)
        )
      }

      // Filter by status
      if (this.filterStatus) {
        result = result.filter(p => p.status === this.filterStatus)
      }

      return result
    }
  },
  methods: {
    statusText(status) {
      const map = {
        completed: '已完成',
        inProgress: '进行中',
        notStarted: '未开始'
      }
      return map[status] || status
    }
  }
}
</script>
